/** The part of `SharedWebSocketService` this helper needs (kept narrow for tests). */
export interface WhenReadyWebSocket {
  isConnectionReady(connectionId: string): boolean;
  subscribe(connectionId: string, topic: string, callback: (message: unknown) => void): string;
  unsubscribe(connectionId: string, subscriptionId: string): void;
}

export interface SubscribeWhenReadyOptions {
  /** Called with false while waiting, true once subscribed. */
  onReadyChange?: (ready: boolean) => void;
  pollMs?: number;
}

/**
 * Subscribe to `topic` on `connectionId` as soon as that connection is ready, polling until it
 * is. Returns a stop function that cancels the polling or unsubscribes.
 *
 * The connection id is the caller's: a shared component must not assume which app it runs in.
 * The telemetry graphs hard-coded `integrator`, a connection inside never opens, so live graphs
 * silently never subscribed there (2026-09-15).
 */
export function subscribeWhenReady(
  ws: WhenReadyWebSocket,
  connectionId: string,
  topic: string,
  onMessage: (message: unknown) => void,
  options: SubscribeWhenReadyOptions = {}
): () => void {
  let subscriptionId: string | null = null;
  let pollHandle: ReturnType<typeof setInterval> | null = null;

  const clearPoll = (): void => {
    if (pollHandle) {
      clearInterval(pollHandle);
      pollHandle = null;
    }
  };

  const tryConnect = (): boolean => {
    if (!ws.isConnectionReady(connectionId)) {
      options.onReadyChange?.(false);
      return false;
    }
    subscriptionId = ws.subscribe(connectionId, topic, onMessage);
    options.onReadyChange?.(true);
    clearPoll();
    return true;
  };

  if (!tryConnect()) {
    pollHandle = setInterval(tryConnect, options.pollMs ?? 500);
  }

  return () => {
    clearPoll();
    if (subscriptionId) {
      ws.unsubscribe(connectionId, subscriptionId);
      subscriptionId = null;
    }
  };
}
